import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { EMAIL_TEMPLATE_EDIT_FLOW_NAME } from '@/features/campaigns/EmailCampaign/constants';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const AUDIENCE_ID = 'a-JXX5hb38ZWJnKidR';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

const EditInit = ({
  workspaceId,
  emailConnectorFlowId,
  connectorInstanceId,
  emailConnectorFlow,
}: {
  workspaceId: string;
  emailConnectorFlowId?: string;
  connectorInstanceId?: string;
  emailConnectorFlow: { connectorId?: string } | undefined;
}) => {
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation('campaign');
  const appDispatch = useAppDispatch();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const history = useHistory();

  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [formData, setFormData] = useState<FlowFormData>({});
  const [mappingData, setMappingData] = useState<FlowFormData>({});

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const [createFlowInstance, { isLoading: isCreatingFlowInstance }] = useCreateFlowInstanceMutation();

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: emailConnectorFlowId,
        audienceId: AUDIENCE_ID,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!emailConnectorFlowId) {
      void fetchPrepareData();
    }
  }, [connectorInstanceId, emailConnectorFlowId, prepareStep, workspaceId, currentLanguage]);

  const handleEditTemplate = async (opts?: CreateTemplateOpts) => {
    const instance = instances?.find((inst) => inst.connectorId === emailConnectorFlow?.connectorId);

    if (!instance) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: emailConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId: AUDIENCE_ID,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: String(t('whatsappTemplates.templateEditedSuccessfully')),
          type: ToastType.SUCCESS,
        })
      );
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      history.push(RouteConstants.emailTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const multiStepFlowFormProps = {
    id: 'email-template-edit-workaround-form',
    showTemplatePreview: true,
    audienceId: AUDIENCE_ID,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: emailConnectorFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData) => {
      setFormData(allParams);
      setMappingData({});
      void handleEditTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
    },
  };

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('whatsappTemplates.editTemplatesHub')}</h2>
      </div>
      <ErrorBoundary>{prepareResult ? <MultiStepFlowForm {...multiStepFlowFormProps} isInAction={isCreatingFlowInstance} /> : null}</ErrorBoundary>
    </div>
  );
};

export const EmailTemplateEditWorkaround = () => {
  const { id: workspaceId } = useWorkspace();

  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const emailConnectorFlow = connectorFlows?.find((flow) => flow.name === EMAIL_TEMPLATE_EDIT_FLOW_NAME);
  const emailConnectorFlowId = emailConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === emailConnectorFlow?.connectorId)?.id;

  if (isConnectorInstancesLoading || isConnectorFlowsLoading) {
    return <Loading />;
  }

  return (
    <EditInit
      workspaceId={workspaceId}
      emailConnectorFlowId={emailConnectorFlowId}
      connectorInstanceId={connectorInstanceId}
      emailConnectorFlow={emailConnectorFlow}
    />
  );
};
